import { Box, IconButton, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { Link, useSearchParams } from 'react-router-dom';
import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';
import { ContentItemActionType } from '../../../../api/types';
import { ArrowBackIcon } from '../../../../assets';
import { useElementDimensions } from '../../../../hooks';
import { ApplicationState } from '../../../../redux/store';
import { useTheme } from '../../../../theme';
import { useTranslate } from '../../../../translations';
import { ACTION_TYPE_TO_TRANSLATION_KEY_MAP } from '../../constants';
import { selectTrainingById, selectSteps, selectTrainingAction } from '../../redux';
import { TrainingGuideNavigationParams } from '../../types';
import { FinishTrainingGuide } from '../FinishTrainingGuide';
import { Pagination } from '../Pagination';
import { Step } from '../Step';
import { useStyles } from './styles';

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

export const TrainingGuide: React.FC = () => {
  const t = useTranslate();
  const params = useParams<TrainingGuideNavigationParams>();
  const trainingId = params.trainingId as string;
  const actionType = params.actionType as ContentItemActionType;
  const [searchParams, setSearchParams] = useSearchParams();
  const step = parseInt(searchParams.get('step') || '0');
  const [headingRef, { height: headerHeight }] = useElementDimensions<HTMLDivElement>();
  let location = useLocation();

  const theme = useTheme();
  const styles = useStyles();

  const training = useSelector((state: ApplicationState) => selectTrainingById(state, trainingId));
  const action = useSelector((state: ApplicationState) => selectTrainingAction(state, trainingId, actionType));
  const steps = useSelector((state: ApplicationState) =>
    selectSteps(state, params.trainingId as string, params.actionType as ContentItemActionType),
  );

  const brandName = location?.pathname?.split('/')[1] || '';

  const onIndexChange = useCallback(
    (nextIndex: number) => {
      setSearchParams({ step: nextIndex.toString() });
    },
    [setSearchParams],
  );

  const titleTail = t(ACTION_TYPE_TO_TRANSLATION_KEY_MAP[params.actionType as ContentItemActionType]);
  const title = `${training?.title} | ${titleTail}`;
  const totalStepsLength = steps.length + 1;

  useEffect(() => {
    // simple out of bound handling
    if (step >= totalStepsLength) {
      onIndexChange(totalStepsLength - 1);
    }
    if (step < 0) {
      onIndexChange(0);
    }
  }, [onIndexChange, step, totalStepsLength]);

  if (!training) {
    return null;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} className={styles.heading} ref={headingRef}>
        <Link to={`/${brandName}`}>
          <IconButton className={styles.backButton}>
            <ArrowBackIcon stroke={theme.palette.black.main} />
          </IconButton>
        </Link>
        <Typography variant="h6">{t(training?.titleKey || '', { defaultValue: title })}</Typography>
      </Stack>
      {/* this lib is recommended by mui https://mui.com/material-ui/react-stepper/#text-with-carousel-effect */}
      <VirtualizeSwipeableViews
        slideRenderer={({ index, key }) => {
          if (index === steps.length) {
            const title = t(action?.finalTitleKey || '', { defaultValue: action?.finalTitle || '' });
            return <FinishTrainingGuide key={key} title={title} />;
          }
          return <Step step={steps[index]} key={key} headerHeight={headerHeight} currentCarouselIndex={step} />;
        }}
        index={step}
        slideCount={totalStepsLength}
        overscanSlideAfter={2}
        overscanSlideBefore={2}
        onChangeIndex={onIndexChange}
        animateHeight
      />
      <Box className={styles.paginationContainer}>
        <Pagination index={step} onIndexPress={onIndexChange} total={totalStepsLength} />
      </Box>
    </Box>
  );
};
